import { FunctionComponent } from "react";

import {
	ContentStatus,
	SegmentContent,
	CompanySegment,
	PersonaSegment,
	SegmentKey,
	SectionName,
	SectionContentStatuses,
} from "../schema/knowledgeDb";
import {
	approveAllSections,
	unapproveAllSections,
	getSegmentOverallStatus,
} from "../utils/transformers";

import { ContentSection } from "./ContentSection";
import { EditableStringListTable } from "./EditableStringListTable";
import { FeatureBenefitTable } from "./FeatureBenefitTable";
import { PainPointsTable } from "./PainPointsTable";

interface ContentAreaProps {
	content: SegmentContent | undefined;
	companySegment: CompanySegment | undefined;
	personaSegment: PersonaSegment | undefined;
	activeSegment: SegmentKey | null;
	onContentChange?: (content: SegmentContent) => void;
}

export const ContentArea: FunctionComponent<ContentAreaProps> = ({
	content,
	companySegment,
	personaSegment,
	activeSegment,
	onContentChange,
}) => {
	if (!activeSegment || !companySegment || !personaSegment) {
		return (
			<div className="flex-1 flex items-center justify-center text-sm text-gray-500 min-h-[400px]">
				Select a segment from the sidebar to view content.
			</div>
		);
	}

	if (!content) {
		return (
			<div className="flex-1 flex items-center justify-center text-sm text-gray-500 min-h-[400px]">
				No content available for this segment yet.
			</div>
		);
	}

	const updateContent = (partial: Partial<SegmentContent>) => {
		if (onContentChange && content) {
			onContentChange({ ...content, ...partial });
		}
	};

	const handleApproveSection = (section: SectionName) => {
		if (!content) {
			return;
		}

		const next: SectionContentStatuses = {
			...content.contentStatuses,
			[section]: ContentStatus.HUMAN_APPROVED,
		};

		updateContent({ contentStatuses: next });
	};

	const handleUnapproveSection = (section: SectionName) => {
		if (!content) {
			return;
		}

		const next: SectionContentStatuses = {
			...content.contentStatuses,
			[section]: ContentStatus.DRAFT,
		};

		updateContent({ contentStatuses: next });
	};

	const handleApproveAll = () => {
		if (!content) {
			return;
		}

		updateContent({ contentStatuses: approveAllSections(content) });
	};

	const handleUnapproveAll = () => {
		if (!content) {
			return;
		}

		updateContent({ contentStatuses: unapproveAllSections(content) });
	};

	const segmentStatus = content ? getSegmentOverallStatus(content) : null;
	const hasAiDrafted = segmentStatus === ContentStatus.AI_DRAFTED;
	const allApproved = segmentStatus === ContentStatus.HUMAN_APPROVED;

	return (
		<div className="flex-1 overflow-y-auto px-6">
			{/* Sticky segment header */}
			<div className="sticky top-0 z-10 bg-white pb-3 mb-4 border-b border-gray-200 py-4">
				<div className="flex items-center justify-between">
					<h2 className="text-ssmd font-bold text-gray-900">
						{companySegment.name}
						<span className="mx-2 text-gray-400">›</span>
						<span className="text-brand-light">{personaSegment.name}</span>
					</h2>
					<div className="flex items-center gap-2">
						{hasAiDrafted && (
							<button
								className="px-3 py-1.5 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
								type="button"
								onClick={handleApproveAll}
							>
								✓ Approve All Sections
							</button>
						)}
						{allApproved && (
							<button
								className="px-3 py-1.5 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
								type="button"
								onClick={handleUnapproveAll}
							>
								✗ Unapprove All Sections
							</button>
						)}
					</div>
				</div>
			</div>

			{/* ICP Profile */}
			<ContentSection
				collapsible
				contentStatus={content.contentStatuses.profile}
				editValue={content.profile}
				title="ICP Profile"
				onApprove={() => handleApproveSection("profile")}
				onDelete={() => updateContent({ profile: "" })}
				onSave={(value) => updateContent({ profile: value })}
				onUnapprove={() => handleUnapproveSection("profile")}
			>
				{content.profile || <span className="text-gray-400 italic">No ICP profile set.</span>}
			</ContentSection>

			{/* Pain Points */}
			<PainPointsTable
				contentStatus={content.contentStatuses.painPoints}
				rows={content.painPoints}
				onApprove={() => handleApproveSection("painPoints")}
				onDelete={() => updateContent({ painPoints: [] })}
				onSave={(rows) => updateContent({ painPoints: rows })}
				onUnapprove={() => handleUnapproveSection("painPoints")}
			/>

			{/* Current Solutions */}
			<ContentSection
				collapsible
				contentStatus={content.contentStatuses.currentSolutions}
				editValue={content.currentSolutions}
				title="Current Solutions & Approaches"
				onApprove={() => handleApproveSection("currentSolutions")}
				onDelete={() => updateContent({ currentSolutions: "" })}
				onSave={(value) => updateContent({ currentSolutions: value })}
				onUnapprove={() => handleUnapproveSection("currentSolutions")}
			>
				{content.currentSolutions || <span className="text-gray-400 italic">No data.</span>}
			</ContentSection>

			{/* Market Trends */}
			<ContentSection
				collapsible
				contentStatus={content.contentStatuses.marketTrends}
				editValue={content.marketTrends}
				title="Market Trends & Competitor Approaches"
				onApprove={() => handleApproveSection("marketTrends")}
				onDelete={() => updateContent({ marketTrends: "" })}
				onSave={(value) => updateContent({ marketTrends: value })}
				onUnapprove={() => handleUnapproveSection("marketTrends")}
			>
				{content.marketTrends || <span className="text-gray-400 italic">No data.</span>}
			</ContentSection>

			{/* Obstacles */}
			<EditableStringListTable
				contentStatus={content.contentStatuses.obstacles}
				items={content.obstacles}
				title="Obstacles They Face"
				onApprove={() => handleApproveSection("obstacles")}
				onDelete={() => updateContent({ obstacles: [] })}
				onSave={(items) => updateContent({ obstacles: items })}
				onUnapprove={() => handleUnapproveSection("obstacles")}
			/>

			{/* Opportunities */}
			<EditableStringListTable
				contentStatus={content.contentStatuses.opportunities}
				items={content.opportunities}
				title="Opportunities & Positive Outcomes"
				onApprove={() => handleApproveSection("opportunities")}
				onDelete={() => updateContent({ opportunities: [] })}
				onSave={(items) => updateContent({ opportunities: items })}
				onUnapprove={() => handleUnapproveSection("opportunities")}
			/>

			{/* Features Benefits Table */}
			<FeatureBenefitTable
				contentStatus={content.contentStatuses.featuresBenefits}
				rows={content.featuresBenefits}
				onApprove={() => handleApproveSection("featuresBenefits")}
				onDelete={() => updateContent({ featuresBenefits: [] })}
				onSave={(rows) => updateContent({ featuresBenefits: rows })}
				onUnapprove={() => handleUnapproveSection("featuresBenefits")}
			/>
		</div>
	);
};
